'use client';

import {
  CART_LOCAL_STORAGE_KEY,
  DONATION_LOCAL_STORAGE_KEY,
  getLocalStorage,
  removeLocalStorage,
  setLocalStorage,
} from '@/utils/local-storage';
import { Order } from '@/utils/types';
import { Context, createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';

export type CartPurchaseMode = 'tickets' | 'plan';

export interface CartPlanContext {
  planId: string;
  crossSellingConfigId?: string;
  originExperienceIds?: string[];
}

/**
 * Cross-sell bundle add-on: o plano "Amigo MASP Família" coexiste com os ingressos
 * no mesmo carrinho (não substitui, diferente do switchToPlanMode). Pago junto, no
 * mesmo checkout (duas cobranças sequenciais). `promotionalPrice`/`fullPrice` em centavos.
 */
export interface CartCrossSellPlan {
  configId: string;
  planId: string;
  title: string;
  promotionalPrice: number;
  fullPrice: number;
}

export const CART_MODE_LOCAL_STORAGE_KEY = 'masp:cart:mode';
export const CART_PLAN_CONTEXT_LOCAL_STORAGE_KEY = 'masp:cart:planContext';
export const CART_CROSS_SELL_PLAN_LOCAL_STORAGE_KEY = 'masp:cart:crossSellPlan';

interface CartContextValue {
  cart: Order[];
  setCart: (cart: Order[]) => void;
  isLoadingCart: boolean;
  donation: number | null;
  setDonation: (donation: number | null) => void;
  purchaseMode: CartPurchaseMode;
  planContext: CartPlanContext | null;
  switchToPlanMode: (ctx: CartPlanContext) => void;
  switchToTicketsMode: () => void;
  crossSellPlan: CartCrossSellPlan | null;
  addCrossSellPlan: (plan: CartCrossSellPlan) => void;
  removeCrossSellPlan: () => void;
}

const CartContext: Context<any> = createContext({});

export const CartProvider = ({ children }: { children: React.ReactNode }) => {
  const [cart, setCart] = useState<Order[]>([]);
  const [donation, setDonation] = useState<number | null>(null);
  const [purchaseMode, setPurchaseMode] = useState<CartPurchaseMode>('tickets');
  const [planContext, setPlanContext] = useState<CartPlanContext | null>(null);
  const [crossSellPlan, setCrossSellPlan] = useState<CartCrossSellPlan | null>(null);
  const [isLoadingCart, setIsLoadingCart] = useState(true);
  const isLoadingDonation = useRef(true);
  const isLoadingMode = useRef(true);
  const isLoadingPlanContext = useRef(true);
  const isLoadingCrossSellPlan = useRef(true);

  useEffect(() => {
    const initialCart = getLocalStorage(CART_LOCAL_STORAGE_KEY) || [];
    setCart(initialCart);
    const initialDonation = getLocalStorage(DONATION_LOCAL_STORAGE_KEY) || null;
    setDonation(initialDonation);
    const initialMode = (getLocalStorage(CART_MODE_LOCAL_STORAGE_KEY) as CartPurchaseMode) || 'tickets';
    setPurchaseMode(initialMode);
    const initialPlanContext = getLocalStorage(CART_PLAN_CONTEXT_LOCAL_STORAGE_KEY) || null;
    setPlanContext(initialPlanContext);
    const initialCrossSellPlan = getLocalStorage(CART_CROSS_SELL_PLAN_LOCAL_STORAGE_KEY) || null;
    setCrossSellPlan(initialCrossSellPlan);
  }, []);

  useEffect(() => {
    if (isLoadingCart) {
      setIsLoadingCart(false);
      return;
    }
    setLocalStorage(CART_LOCAL_STORAGE_KEY, cart);
  }, [cart]);

  useEffect(() => {
    if (isLoadingDonation.current) {
      isLoadingDonation.current = false;
      return;
    }

    if (donation) {
      setLocalStorage(DONATION_LOCAL_STORAGE_KEY, donation);
    } else {
      removeLocalStorage(DONATION_LOCAL_STORAGE_KEY);
    }
  }, [donation]);

  useEffect(() => {
    if (isLoadingMode.current) {
      isLoadingMode.current = false;
      return;
    }
    setLocalStorage(CART_MODE_LOCAL_STORAGE_KEY, purchaseMode);
  }, [purchaseMode]);

  useEffect(() => {
    if (isLoadingPlanContext.current) {
      isLoadingPlanContext.current = false;
      return;
    }
    if (planContext) {
      setLocalStorage(CART_PLAN_CONTEXT_LOCAL_STORAGE_KEY, planContext);
    } else {
      removeLocalStorage(CART_PLAN_CONTEXT_LOCAL_STORAGE_KEY);
    }
  }, [planContext]);

  useEffect(() => {
    if (isLoadingCrossSellPlan.current) {
      isLoadingCrossSellPlan.current = false;
      return;
    }
    if (crossSellPlan) {
      setLocalStorage(CART_CROSS_SELL_PLAN_LOCAL_STORAGE_KEY, crossSellPlan);
    } else {
      removeLocalStorage(CART_CROSS_SELL_PLAN_LOCAL_STORAGE_KEY);
    }
  }, [crossSellPlan]);

  const switchToPlanMode = useCallback((ctx: CartPlanContext) => {
    setCart([]);
    setDonation(null);
    setPlanContext(ctx);
    setPurchaseMode('plan');
  }, []);

  const switchToTicketsMode = useCallback(() => {
    setPlanContext(null);
    setPurchaseMode('tickets');
  }, []);

  // Cross-sell bundle: adiciona/remove o plano como item que coexiste com os ingressos.
  const addCrossSellPlan = useCallback((plan: CartCrossSellPlan) => {
    setCrossSellPlan(plan);
  }, []);

  const removeCrossSellPlan = useCallback(() => {
    setCrossSellPlan(null);
  }, []);

  const contextValue = useMemo<CartContextValue>(
    () => ({
      cart,
      setCart,
      isLoadingCart,
      donation,
      setDonation,
      purchaseMode,
      planContext,
      switchToPlanMode,
      switchToTicketsMode,
      crossSellPlan,
      addCrossSellPlan,
      removeCrossSellPlan,
    }),
    [
      cart,
      isLoadingCart,
      donation,
      purchaseMode,
      planContext,
      switchToPlanMode,
      switchToTicketsMode,
      crossSellPlan,
      addCrossSellPlan,
      removeCrossSellPlan,
    ]
  );

  const cartItemCount = cart.reduce((acc: number, order: Order) => acc + (order.tickets?.length || 0), 0);

  return (
    <CartContext.Provider value={contextValue}>
      {children}
      {/* ARIA live region for screen reader cart announcements */}
      <div aria-live='polite' aria-atomic='true' className='sr-only'>
        {cartItemCount > 0 ? `${cartItemCount} ${cartItemCount === 1 ? 'item no carrinho' : 'itens no carrinho'}` : ''}
      </div>
    </CartContext.Provider>
  );
};

export default CartContext;
